import React, { useMemo, useState, useEffect } from 'react';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import useRosterCatalog from '../hooks/useRosterCatalog';
import {
  TEAM_LABELS,
  ROSTER_POSITIONS,
  PHOTO_POSITION_PRESETS,
  staticRosterAsDocs,
  rosterPlayerWritePayload,
  playerSlug,
  overrideDocId,
} from '../utils/rosterCatalog';
import placeholder from '../assets/images/generic-player.png';
import './RosterAdmin.css';

const emptyForm = (teamId = '2014') => ({
  teamId,
  name: '',
  number: '',
  position: 'Forward',
  bio: '',
  funFact: '',
  photoUrl: '',
  photoPosition: 'center center',
  hasPhotoPermission: true,
});

export default function RosterAdmin() {
  const { players, players2013, players2014, source, loading } = useRosterCatalog();
  const [teamTab, setTeamTab] = useState('2014');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm('2014'));
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('');

  const teamPlayers = teamTab === '2013' ? players2013 : players2014;

  const filteredPlayers = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return teamPlayers;
    return teamPlayers.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        String(p.number).includes(q) ||
        String(p.position || '')
          .toLowerCase()
          .includes(q)
    );
  }, [teamPlayers, filter]);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const resetForm = (teamId = teamTab) => {
    setEditingId(null);
    setForm(emptyForm(teamId));
    setPhotoFile(null);
    setPhotoPreview('');
  };

  const startAdd = () => {
    resetForm(teamTab);
    setMessage({ type: '', text: '' });
  };

  const startEdit = (player) => {
    setEditingId(player.id);
    setForm({
      teamId: player.teamId,
      name: player.name,
      number: player.number,
      position: player.position || 'Forward',
      bio: player.bio || '',
      funFact: player.funFact || '',
      photoUrl: player.photoUrl || '',
      photoPosition: player.photoPosition || 'center center',
      hasPhotoPermission: player.hasPhotoPermission !== false,
    });
    setPhotoFile(null);
    setPhotoPreview('');
    setMessage({ type: '', text: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const syncParentLinksForPlayer = async (playerKey, nextName, teamId) => {
    const parentsSnap = await getDocs(collection(db, 'parents'));
    const batch = writeBatch(db);
    let updates = 0;
    parentsSnap.forEach((parentDoc) => {
      const data = parentDoc.data() || {};
      const keys = Array.isArray(data.linkedPlayerKeys) ? data.linkedPlayerKeys : [];
      const links = Array.isArray(data.linkedPlayers) ? data.linkedPlayers : [];
      if (!keys.includes(playerKey)) return;
      let changed = false;
      const nextLinks = links.map((lp) => {
        if (!lp) return lp;
        const lpKey =
          lp.key ||
          (lp.teamId && lp.playerName ? overrideDocId(lp.teamId, lp.playerName) : '');
        if (lpKey === playerKey) {
          if (lp.playerName !== nextName || lp.teamId !== teamId) {
            changed = true;
            return { teamId, playerName: nextName, key: playerKey };
          }
        }
        return lp;
      });
      if (changed) {
        batch.update(parentDoc.ref, { linkedPlayers: nextLinks });
        updates += 1;
      }
    });
    if (updates > 0) await batch.commit();
    return updates;
  };

  const removePlayerFromParentLinks = async (playerKey) => {
    const parentsSnap = await getDocs(collection(db, 'parents'));
    const batch = writeBatch(db);
    let updates = 0;
    parentsSnap.forEach((parentDoc) => {
      const data = parentDoc.data() || {};
      const keys = Array.isArray(data.linkedPlayerKeys) ? [...data.linkedPlayerKeys] : [];
      const links = Array.isArray(data.linkedPlayers) ? [...data.linkedPlayers] : [];
      const nextKeys = keys.filter((k) => k !== playerKey);
      const nextLinks = links.filter((lp) => {
        if (!lp) return false;
        const lpKey =
          lp.key ||
          (lp.teamId && lp.playerName ? overrideDocId(lp.teamId, lp.playerName) : '');
        return lpKey !== playerKey;
      });
      if (nextKeys.length !== keys.length || nextLinks.length !== links.length) {
        batch.update(parentDoc.ref, {
          linkedPlayerKeys: nextKeys,
          linkedPlayers: nextLinks,
        });
        updates += 1;
      }
    });
    if (updates > 0) await batch.commit();
    return updates;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    const name = String(form.name || '').trim();
    if (!name) {
      setMessage({ type: 'error', text: 'Player name is required.' });
      return;
    }
    if (!form.teamId || (form.teamId !== '2013' && form.teamId !== '2014')) {
      setMessage({ type: 'error', text: 'Select 12U or 14U.' });
      return;
    }
    if (photoFile && !photoFile.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Photo must be an image.' });
      return;
    }
    if (photoFile && photoFile.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Photo must be 10 MB or smaller.' });
      return;
    }

    const existing = editingId ? players.find((p) => p.id === editingId) : null;
    const slug = existing?.playerSlug || playerSlug(name);
    const docId = existing?.id || `${form.teamId}__${slug}`;

    // Prevent colliding with another player's id when adding
    if (!existing && players.some((p) => p.id === docId)) {
      setMessage({
        type: 'error',
        text: 'A player with that name already exists on this team. Edit the existing player instead.',
      });
      return;
    }

    setSaving(true);
    try {
      let photoUrl = form.photoUrl || '';
      if (photoFile) {
        const user = auth.currentUser;
        if (!user) throw new Error('Not signed in');
        const rawExt = (photoFile.name.split('.').pop() || 'jpg').toLowerCase();
        const ext = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(rawExt) ? rawExt : 'jpg';
        const path = `rosterPhotos/${user.uid}/${form.teamId}/${slug}/${Date.now()}.${ext}`;
        const sref = ref(storage, path);
        await uploadBytes(sref, photoFile, { contentType: photoFile.type || 'image/jpeg' });
        photoUrl = await getDownloadURL(sref);
      }

      const payload = rosterPlayerWritePayload(
        {
          ...form,
          name,
          photoUrl,
          number: form.number === '' ? 0 : Number(form.number),
        },
        { existingSlug: slug }
      );
      payload.updatedAt = Timestamp.now();
      payload.updatedBy = auth.currentUser?.uid || '';

      await setDoc(doc(db, 'rosterPlayers', docId), payload, { merge: true });

      if (existing && existing.name !== name) {
        await syncParentLinksForPlayer(docId, name, form.teamId);
      }

      setMessage({
        type: 'success',
        text: existing ? `Updated ${name}.` : `Added ${name} to the ${TEAM_LABELS[form.teamId]} roster.`,
      });
      resetForm(form.teamId);
      setTeamTab(form.teamId);
    } catch (err) {
      console.error(err);
      setMessage({
        type: 'error',
        text: 'Could not save player. Deploy latest Firestore/Storage rules if this is the first time using Team Roster.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (player) => {
    const ok = window.confirm(
      `Remove ${player.name} (#${player.number}) from the ${TEAM_LABELS[player.teamId] || player.teamId} roster?\n\nThis also removes parent profile links for this player and their public bio override. Season stats in the spreadsheet are unchanged.`
    );
    if (!ok) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      // Soft-delete so built-in catalog players do not reappear after remove
      const slug = player.playerSlug || playerSlug(player.name);
      const payload = rosterPlayerWritePayload(
        {
          teamId: player.teamId,
          name: player.name,
          number: player.number,
          position: player.position,
          bio: player.bio || '',
          funFact: player.funFact || '',
          photoUrl: typeof player.photoUrl === 'string' ? player.photoUrl : '',
          photoPosition: player.photoPosition || 'center center',
          hasPhotoPermission: player.hasPhotoPermission !== false,
          sortOrder: player.sortOrder,
          active: false,
        },
        { existingSlug: slug }
      );
      payload.updatedAt = Timestamp.now();
      payload.updatedBy = auth.currentUser?.uid || '';
      await setDoc(doc(db, 'rosterPlayers', player.id), payload, { merge: true });
      try {
        await deleteDoc(doc(db, 'rosterOverrides', player.id));
      } catch (e) {
        /* override may not exist */
      }
      await removePlayerFromParentLinks(player.id);
      if (editingId === player.id) resetForm(player.teamId);
      setMessage({ type: 'success', text: `Removed ${player.name} from the roster.` });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Could not remove player.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSeedStatic = async () => {
    if (source === 'firestore' && players.length > 0) {
      const ok = window.confirm(
        'Firestore already has roster players. Import only missing players from the built-in list (will not overwrite existing docs)?'
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(
        'Import the built-in 12U and 14U roster into Firestore so you can add/edit/remove players here? Existing public roster will then use Firestore.'
      );
      if (!ok) return;
    }

    setSeeding(true);
    setMessage({ type: '', text: '' });
    try {
      const seed = staticRosterAsDocs();
      // Include ids already in Firestore (active or soft-deleted) by reading collection
      const existingSnap = await getDocs(collection(db, 'rosterPlayers'));
      const existingIds = new Set(existingSnap.docs.map((d) => d.id));
      let written = 0;
      // Batches max 500
      let batch = writeBatch(db);
      let ops = 0;
      for (const p of seed) {
        if (existingIds.has(p.id)) continue;
        const payload = rosterPlayerWritePayload(p, { existingSlug: p.playerSlug });
        // Keep bundled photo paths from the static catalog
        if (p.photoUrl) payload.photoUrl = p.photoUrl;
        payload.updatedAt = Timestamp.now();
        payload.updatedBy = auth.currentUser?.uid || 'seed';
        batch.set(doc(db, 'rosterPlayers', p.id), payload, { merge: false });
        written += 1;
        ops += 1;
        if (ops >= 400) {
          await batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      }
      if (ops > 0) await batch.commit();
      setMessage({
        type: 'success',
        text:
          written === 0
            ? 'All built-in players are already in Firestore.'
            : `Imported ${written} player${written === 1 ? '' : 's'} into Firestore.`,
      });
    } catch (err) {
      console.error(err);
      setMessage({
        type: 'error',
        text: 'Import failed. Deploy Firestore rules for rosterPlayers, then try again.',
      });
    } finally {
      setSeeding(false);
    }
  };

  const previewSrc = photoPreview || form.photoUrl || placeholder;

  return (
    <div className="roster-admin">
      <h2 className="admin-main-section-title">Team roster</h2>
      <p className="roster-admin-lead">
        Add, edit, or remove players on the public roster. Parents can still update photo / about / fun fact for
        kids you link under Parent accounts. Stats stay in the spreadsheet.
      </p>

      <div className="roster-admin-toolbar">
        <div className="roster-admin-source">
          {loading ? (
            <span>Loading roster…</span>
          ) : source === 'firestore' ? (
            <span>
              Live source: <strong>Firestore</strong> ({players.length} players)
            </span>
          ) : source === 'hybrid' ? (
            <span>
              Live source: <strong>Firestore + built-in</strong> ({players.length} players). Edits save to
              Firestore; the rest of the built-in list still shows until you import or edit them.
            </span>
          ) : (
            <span>
              Live source: <strong>built-in list</strong> ({players.length} players). Adding or editing a
              player saves to Firestore without hiding the rest.
            </span>
          )}
        </div>
        <button
          type="button"
          className="admin-button"
          disabled={seeding || saving}
          onClick={handleSeedStatic}
        >
          {seeding ? 'Importing…' : 'Import built-in roster'}
        </button>
      </div>

      {message.text ? (
        <p className={`roster-admin-message ${message.type === 'success' ? 'is-success' : 'is-error'}`}>
          {message.text}
        </p>
      ) : null}

      <form className="roster-admin-form" onSubmit={handleSave}>
        <h3>{editingId ? 'Edit player' : 'Add player'}</h3>
        <div className="roster-admin-form-grid">
          <label>
            Team
            <select
              className="admin-input"
              value={form.teamId}
              disabled={!!editingId}
              onChange={(e) => updateField('teamId', e.target.value)}
            >
              <option value="2014">14U</option>
              <option value="2013">12U</option>
            </select>
          </label>
          <label>
            Name
            <input
              className="admin-input"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
              placeholder="Player name"
            />
          </label>
          <label>
            Number
            <input
              className="admin-input"
              type="number"
              min="0"
              max="99"
              value={form.number}
              onChange={(e) => updateField('number', e.target.value)}
              required
            />
          </label>
          <label>
            Position
            <select
              className="admin-input"
              value={form.position}
              onChange={(e) => updateField('position', e.target.value)}
            >
              {ROSTER_POSITIONS.map((pos) => (
                <option key={pos} value={pos}>
                  {pos}
                </option>
              ))}
            </select>
          </label>
          <label>
            Photo crop position
            <select
              className="admin-input"
              value={form.photoPosition}
              onChange={(e) => updateField('photoPosition', e.target.value)}
            >
              {PHOTO_POSITION_PRESETS.map((pos) => (
                <option key={pos} value={pos}>
                  {pos}
                </option>
              ))}
            </select>
          </label>
          <label className="roster-admin-check">
            <input
              type="checkbox"
              checked={form.hasPhotoPermission}
              onChange={(e) => updateField('hasPhotoPermission', e.target.checked)}
            />
            Show photo on public roster
          </label>
        </div>

        <div className="roster-admin-photo-row">
          <img src={previewSrc} alt="" className="roster-admin-photo-preview" />
          <div>
            <p className="roster-admin-hint">
              Portrait ~900×1000 px recommended. JPEG/PNG, max 10 MB. Parents may replace this later if linked.
            </p>
            <label className="roster-admin-file">
              <span className="admin-button">Choose photo</span>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  setPhotoFile(e.target.files?.[0] || null);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>

        <label className="roster-admin-block">
          About
          <textarea
            className="admin-input"
            rows={4}
            value={form.bio}
            onChange={(e) => updateField('bio', e.target.value)}
            placeholder="Short bio for the roster panel"
          />
        </label>
        <label className="roster-admin-block">
          Fun fact
          <textarea
            className="admin-input"
            rows={2}
            value={form.funFact}
            onChange={(e) => updateField('funFact', e.target.value)}
            placeholder="Optional fun fact"
          />
        </label>

        <div className="roster-admin-form-actions">
          <button type="submit" className="admin-button" disabled={saving || seeding}>
            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add to roster'}
          </button>
          {editingId ? (
            <button type="button" className="admin-button" disabled={saving} onClick={() => startAdd()}>
              Cancel edit
            </button>
          ) : null}
        </div>
      </form>

      <div className="roster-admin-list-header">
        <div className="roster-admin-tabs">
          <button
            type="button"
            className={`admin-segment-button${teamTab === '2014' ? ' is-active' : ''}`}
            onClick={() => {
              setTeamTab('2014');
              if (!editingId) setForm((f) => ({ ...f, teamId: '2014' }));
            }}
          >
            14U ({players2014.length})
          </button>
          <button
            type="button"
            className={`admin-segment-button${teamTab === '2013' ? ' is-active' : ''}`}
            onClick={() => {
              setTeamTab('2013');
              if (!editingId) setForm((f) => ({ ...f, teamId: '2013' }));
            }}
          >
            12U ({players2013.length})
          </button>
        </div>
        <input
          className="admin-input roster-admin-filter"
          placeholder="Filter by name, number, position…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="roster-admin-table-wrap">
        <table className="admin-table roster-admin-table">
          <thead>
            <tr>
              <th className="roster-admin-th-photo">Photo</th>
              <th className="roster-admin-th-num">#</th>
              <th className="roster-admin-th-name">Name</th>
              <th className="roster-admin-th-pos">Position</th>
              <th className="roster-admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPlayers.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ color: '#888' }}>
                  No players on this team yet.
                </td>
              </tr>
            ) : (
              filteredPlayers.map((p) => (
                <tr key={p.id}>
                  <td className="roster-admin-td-photo">
                    <img
                      src={p.hasPhotoPermission ? p.photoUrl || placeholder : placeholder}
                      alt=""
                      className="roster-admin-thumb"
                      style={p.photoPosition ? { objectPosition: p.photoPosition } : undefined}
                    />
                  </td>
                  <td className="roster-admin-td-num">{p.number}</td>
                  <td className="roster-admin-td-name">
                    <span className="roster-admin-player-name">{p.name}</span>
                    {p.bio ? (
                      <span className="roster-admin-player-bio" title={p.bio}>
                        {p.bio}
                      </span>
                    ) : null}
                  </td>
                  <td className="roster-admin-td-pos">{p.position}</td>
                  <td className="roster-admin-td-actions">
                    <div className="roster-admin-row-actions">
                      <button
                        type="button"
                        className="admin-button roster-admin-action-btn"
                        onClick={() => startEdit(p)}
                        disabled={saving}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-button-archive roster-admin-action-btn"
                        onClick={() => handleDelete(p)}
                        disabled={saving}
                        title="Remove from roster"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
